import React, { useContext, useEffect, useState } from 'react';
import { FirebaseContext } from 'common';
import { useSelector } from 'react-redux';
import { onValue, off } from 'firebase/database';
import { Alert, Collapse, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { formatAddisTime } from '../common/exportUtils';

const INTERNAL_ROLES = ['admin', 'fleetadmin', 'callcenteragent', 'callcentersupervisor', 'finance', 'marketing'];

export default function OutageBanner() {
  const { t } = useTranslation();
  const { serviceOutagesRef } = useContext(FirebaseContext);
  const auth = useSelector(state => state.auth);
  const role = auth?.profile?.usertype;
  const isInternalUser = role && INTERNAL_ROLES.includes(role);

  const [outages, setOutages] = useState({});
  const [dismissed, setDismissed] = useState({});

  useEffect(() => {
    if (!serviceOutagesRef || !isInternalUser) return;
    const unsubscribe = onValue(serviceOutagesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setOutages(data);
        // Auto-clear dismissed state for resolved outages
        setDismissed((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((key) => {
            if (!data[key] || !data[key].active) {
              delete next[key];
            }
          });
          return next;
        });
      } else {
        setOutages({});
      }
    });
    return () => off(serviceOutagesRef);
  }, [serviceOutagesRef, isInternalUser]);

  if (!isInternalUser) return null;

  const isFalsePositive = (errorStr) => {
    if (!errorStr) return false;
    try {
      const parsed = JSON.parse(errorStr);
      if (parsed && parsed.status === 200) return true;
    } catch (e) {
      // not JSON — check for raw pattern
      if (/"status"\s*:\s*200/.test(errorStr)) return true;
    }
    return false;
  };

  const activeOutages = Object.entries(outages).filter(
    ([key, val]) => val && val.active && !dismissed[key] && !isFalsePositive(val.error)
  );

  if (activeOutages.length === 0) return null;

  const serviceLabels = { sms: 'SMS' };

  return (
    <div style={{ marginBottom: 16 }}>
      {activeOutages.map(([key, outage]) => (
        <Collapse in key={key}>
          <Alert
            severity="error"
            variant="filled"
            action={
              <IconButton
                aria-label={t('close_message', { defaultValue: 'Close message' })}
                color="inherit"
                size="small"
                onClick={() => setDismissed((prev) => ({ ...prev, [key]: true }))}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{ borderRadius: 0 }}
          >
            <strong>{t('outage_banner_title', { service: serviceLabels[key] || key, defaultValue: '{{service}} service outage' })}</strong>
            {' — '}
            {outage.error || t('outage_banner_unavailable', { defaultValue: 'Service is currently unavailable.' })}
            {outage.lastErrorAt && (
              <span style={{ marginLeft: 8, opacity: 0.8, fontSize: '0.85em' }}>
                {t('outage_banner_since', { time: formatAddisTime(outage.lastErrorAt), defaultValue: '(since {{time}})' })}
              </span>
            )}
          </Alert>
        </Collapse>
      ))}
    </div>
  );
}
